<template>
  <div>
    <div v-show="isLoading && isOpenModal">
      <spin-loader />
    </div>
    <div v-show="!isLoading">
      <validation-observer
        v-slot="{ handleSubmit, invalid }"
        ref="refFormObserver"
      >
        <b-modal
          id="idModalContract"
          ref="modalContract"
          no-close-on-backdrop
          no-close-on-esc
          centered
          size="lg"
          cancel-variant="outline-primary"
          footer-bg-variant="light"
          :ok-disabled="invalid"
          @ok="handleSubmit(save)"
          @cancel="cancel()"
          @hidden="cancel()"
        >
          <template #modal-title>
            Busque uma Licitação Homologada
          </template>
          <template #modal-cancel>
            Cancelar
          </template>
          <template #modal-ok>
            Vincular
          </template>
          <b-form
            class="px-2 mt-1"
          >
            <b-row>
              <b-col
                cols="12"
              >
                <bidding-select
                  id="bidding"
                  v-model="data.bidding_set"
                  :required-field="true"
                  :filter="filterBidding"
                  title="Selecione uma Licitação Homologada"
                  @ready="syncLoad.ready('bidding')"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="contract_number"
                  :rules="{
                    required: true,
                    regex:/^[a-zA-Z0-9\-/]+$/
                  }"
                >
                  <b-form-group
                    label="Nº da Ata/Contrato"
                    label-for="contract_number"
                  >
                    <b-form-input
                      id="contract_number"
                      v-model="data.number"
                      :state="getValidationState(validationContext)"
                      name="contract_number"
                      :placeholder="'Nº da Ata/Contrato'"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col
                cols="12"
                md="4"
              >
                <contract-type-select
                  id="idContract"
                  v-model="data.type_set"
                  :required-field="true"
                  @ready="syncLoad.ready('documentType')"
                />
              </b-col>
              <b-col
                cols="12"
                md="4"
              >
                <scope-select
                  id="idScope"
                  v-model="data.scope_set"
                  :required-field="true"
                  @ready="syncLoad.ready('scope')"
                />
              </b-col>
            </b-row>
          </b-form>
        </b-modal>
      </validation-observer>
    </div>
    <div>
      <b-button
        id="addContractBtn"
        v-b-tooltip.hover
        v-b-modal.idModalContract
        class="btn-icon rounded-circle m-3"
        variant="primary"
        size="md"
        :disabled="!hasPermissionToWrite"
        title="Adicionar Contrato"
      >
        <svg-icon
          type="mdi"
          :path="mdiPlus"
        />
      </b-button>
    </div>
  </div>
</template>
<script>
import {
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  VBTooltip,
} from 'bootstrap-vue'

import {
  ValidationObserver,
  ValidationProvider,
} from 'vee-validate'

import {
  mdiPlus,
} from '@mdi/js'

import formValidation from '@core/comp-functions/forms/form-validation'
import SvgIcon from '@jamescoyle/vue-icon'
import SpinLoader from '@/components/spinloader/SpinLoader.vue'
import syslic from '@/syslic'
import SyncLoad from '@/components/utils/syncLoad'
import BiddingSelect from '@/components/bidding/biddingSelect.vue'
import ContractTypeSelect from '@/components/contract/ContractTypeSelect.vue'
import ScopeSelect from '@/components/contract/ScopeSelect.vue'

export default {
  components: {
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    ValidationObserver,
    ValidationProvider,
    SvgIcon,
    SpinLoader,
    BiddingSelect,
    ContractTypeSelect,
    ScopeSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  model: {
    prop: 'contractValue',
    event: 'contractValueChange',
  },
  props: {
    contractValue: {
      type: [Object, String],
      required: false,
      default: null,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    isAdd: {
      type: Boolean,
      required: true,
      default: true,
    },
  },
  data() {
    const isOpenModal = false
    const isLoading = true

    const {
      refFormObserver,
      getValidationState,
    } = formValidation(this.data)

    const syncLoad = new SyncLoad()
    syncLoad.addTrigger('documentType')
    syncLoad.addTrigger('scope')
    syncLoad.addTrigger('bidding')

    syncLoad.onReady(() => {
      this.isLoading = false
    })

    const enabledFieldsAddress = [
      'zip_code',
      'address',
      'number',
      'complement',
      'neighborhood',
      'state',
      'city',
      'country',
    ]

    const filterBidding = {
      is_homologated: true,
    }

    return {
      refFormObserver,
      getValidationState,
      isOpenModal,
      isLoading,
      syncLoad,
      enabledFieldsAddress,
      filterBidding,
    }
  },
  computed: {
    data: {
      get() {
        return this.contractValue
      },
      set(value) {
        this.$emit('contractValueChange', value)
      },
    },
    hasPermissionToWrite() {
      const profile = syslic.authentication.getProfile()
      return profile.get_permissions_for_modules.contract.can_write
    },
  },
  watch: {
    'data.type_set': function updateDocumentType(val) {
      if (val) {
        this.data.type = val.id
      } else {
        this.data.type = ''
        this.data.type_set = ''
      }
    },
    'data.scope_set': function updateScope(val) {
      if (val) {
        this.data.scope = val.id
      } else {
        this.data.scope = ''
        this.data.scope_set = ''
      }
    },
    'data.bidding_set': function updateBidding(val) {
      if (val) {
        this.data.bidding = val.id
      } else {
        this.data.bidding = ''
        this.data.bidding_set = ''
      }
    },
  },
  methods: {
    openModal() {
      this.isOpenModal = true
      this.$refs.modalContract.show()
    },
    save() {
      this.$emit('save')
    },
    cancel() {
      this.isOpenModal = false
      this.data = {
        bidding: '',
        bidding_set: '',
        date_finish: null,
        date_start: null,
        file_set: [],
        id: '',
        number: '',
        observation: '',
        owner: '',
        owner_set: '',
        state: 'draft',
        scope: '',
        scope_set: '',
        status: '',
        status_set: '',
        type: '',
        type_set: '',
      }
      this.$emit('isAdd', true)
    },
  },
  setup() {
    return {
      mdiPlus,
    }
  },
}
</script>
<style lang="scss">
@import "@core/scss/base/core/colors/palette-variables.scss";

#addContractBtn {
  position: fixed;
  right: 0;
  bottom: 0;
}

 #idModalContract {
  .modal-body{
    padding: 0 !important;
  }
  .modal-header .modal-title{
    font-size: 1.2em;
    color: $primary !important;
    font-weight: bolder;
  }
 }

 #idModalContract .modal-footer {
  display: flex !important;
  flex-direction: row-reverse !important;
  justify-content: end !important;
  background-color: #fff !important;

  .btn{
    min-width: 107px;
  }
}

[dir] .modal-footer {
  border: 0px !important;
  background-color: #fff !important;
}
</style>
